import { patientIdSchema } from "@repo/api/schemas";
import {
  completeQuestionnaire,
  updateCondition,
  useConditions,
  useNotes,
  usePatient,
  useQuestionnaireResponseGroups,
  useSymptoms,
} from "@repo/data-store";
import type { QuestionnaireInstanceId } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { Activity, Brain, Flag, type LucideIcon, Pill, Plus, TriangleAlert } from "lucide-react";
import { useCallback, useMemo } from "react";
import { openOverlay } from "@/app/overlays";
import { PatientSidePanel } from "@/app/surfaces/patient-history/patient-side-panel";
import { partitionProblemList } from "@/app/surfaces/patient-history/problem-list-partition";
import { NotePanel } from "@/app/surfaces/workbench/note-panel";
import { QuestionnaireCard } from "@/app/surfaces/workbench/questionnaire-card";
import { WorkbenchTreatmentPlanSection } from "@/app/surfaces/workbench/treatment-plan-section";
import { WorkbenchSection, WorkbenchSectionEmpty } from "@/app/surfaces/workbench/workbench-section";
import { noteEntries } from "@/features/notes";
import { ConditionCard, sortByEffectiveDesc, sortByOnsetDesc } from "@/features/patients";
import { DetailAccordion } from "@/shared/common/detail-accordion";

export const Route = createFileRoute("/patients/$patientId/_layout/workbench")({
  component: RouteComponent,
});

/**
 * Clinical Workbench — the deep working surface for a patient, rendered inside
 * the patient `_layout` (header + tab strip persist; Overview stays the active
 * tab). v1 reuses the existing Overview compound lists in a two-column layout.
 * Bespoke cards (editable note, by-diagnosis manifestations, meds/allergies
 * rail) are deferred — see handoff TODO(data) items.
 */
function RouteComponent() {
  const patientId = patientIdSchema.parse(Route.useParams().patientId);
  const { patient } = usePatient(patientId);
  const { conditions } = useConditions({ patientId });
  const { symptoms } = useSymptoms({ patientId });
  const questionnaireGroups = useQuestionnaireResponseGroups({ patientId });
  const { notes, isReady: notesReady } = useNotes({ patientId });

  const noteEntriesForPanel = useMemo(() => noteEntries(notes), [notes]);

  const { active: problemListConditions } = useMemo(() => partitionProblemList(conditions), [conditions]);
  const sortedSymptoms = useMemo(() => sortByEffectiveDesc(symptoms), [symptoms]);

  const sortedConditions = useMemo(() => sortByOnsetDesc(problemListConditions), [problemListConditions]);
  const symptomsById = useMemo(() => new Map(symptoms.map((s) => [s.observationId, s])), [symptoms]);
  const symptomsForCondition = useCallback(
    (c: (typeof sortedConditions)[number]) =>
      (c.evidenceObservationIds ?? []).flatMap((id) => {
        const s = symptomsById.get(id);
        return s ? [s] : [];
      }),
    [symptomsById],
  );

  // Finalize a response — domain guard (required items, status) errors surface
  // globally via the tRPC error link; the completed status syncs back and flips
  // the card to read-only.
  const handleCompleteQr = useCallback(async (instanceId: QuestionnaireInstanceId) => {
    await completeQuestionnaire({ instanceId }).isPersisted.promise;
  }, []);

  const handleAddCondition = useCallback(() => openOverlay("patient.condition", { patientId }), [patientId]);
  const handleAddSymptom = useCallback(
    () => openOverlay("patient.symptom", { patientId, preselectedConditionId: null }),
    [patientId],
  );

  if (!patient) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col">
      <WorkbenchBar
        diagnoses={problemListConditions.length}
        symptoms={sortedSymptoms.length}
        assessments={questionnaireGroups[0]?.totalResponseCount ?? 0}
      />

      <div className="grid flex-1 grid-cols-1 bg-sidebar lg:grid-cols-[1fr_auto]">
        {/* LEFT — structured clinical picture */}
        <div className="min-w-0 space-y-4 bg-background px-5 pb-8 pt-5">
          <QuickActions
            onAddSymptom={handleAddSymptom}
            onAddCondition={handleAddCondition}
            onAddFlag={() => openOverlay("patient.flag", { patientId, editing: null })}
            onAddAllergy={() => openOverlay("patient.allergy", { patientId, editing: null })}
            onAddMedication={() => openOverlay("patient.medication", { patientId, editing: null })}
          />

          {/* Editable note, one at a time with a picker — saves and locks as
              the Notes tab does. Data lifted to the route. */}
          <NotePanel patientId={patientId} entries={noteEntriesForPanel} isReady={notesReady} />

          <WorkbenchSection
            title="Problem list"
            action={
              <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2.5 text-sm" onClick={handleAddCondition}>
                <Plus className="size-3" />
                Add
              </Button>
            }
          >
            {sortedConditions.length === 0 ? (
              <WorkbenchSectionEmpty>No problems recorded.</WorkbenchSectionEmpty>
            ) : (
              <div className="space-y-3">
                {sortedConditions.map((c) => (
                  <ConditionCard
                    key={c.conditionId}
                    condition={c}
                    symptoms={symptomsForCondition(c)}
                    onAddSymptom={() =>
                      openOverlay("patient.symptom", {
                        patientId,
                        preselectedConditionId: c.conditionId,
                      })
                    }
                    onEdit={() => openOverlay("patient.condition", { patientId, editing: c })}
                    onResolve={() => updateCondition({ conditionId: c.conditionId, clinicalStatus: "resolved" })}
                    onEditSymptom={(s) =>
                      openOverlay("patient.symptom", { patientId, preselectedConditionId: null, editing: s })
                    }
                  />
                ))}
              </div>
            )}
          </WorkbenchSection>

          <WorkbenchSection title="Questionnaires">
            {questionnaireGroups.length === 0 ? (
              <WorkbenchSectionEmpty>No questionnaires filed.</WorkbenchSectionEmpty>
            ) : (
              <DetailAccordion
                variant="cards"
                defaultValue={questionnaireGroups
                  .filter((group) => group.followUpInstanceId)
                  .map((group) => group.definitionUrl)}
              >
                {questionnaireGroups.map((group) => (
                  <QuestionnaireCard key={group.definitionUrl} group={group} onComplete={handleCompleteQr} />
                ))}
              </DetailAccordion>
            )}
          </WorkbenchSection>

          <WorkbenchTreatmentPlanSection patientId={patientId} />
        </div>

        {/* RIGHT — the patient history panel, shared with visit charting. */}
        {/* No field history: it follows the field focused in a visit chart. */}
        <PatientSidePanel
          patientId={patientId}
          sections={["history", "prior-visit", "assessments", "care-plan"]}
          className="border-l"
        />
      </div>
    </div>
  );
}

// ── Bar: title · stats (scrolls away; the tab strip is the sticky anchor) ──
function WorkbenchBar({
  diagnoses,
  symptoms,
  assessments,
}: {
  diagnoses: number;
  symptoms: number;
  assessments: number;
}) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-3 border-b bg-background px-5">
      <span className="text-base font-semibold">Clinical workbench</span>
      <div className="ml-2 hidden items-center gap-3.5 md:flex">
        {(
          [
            [diagnoses, "diagnoses"],
            [symptoms, "symptoms"],
            [assessments, "assessments"],
          ] as const
        ).map(([n, label], i) => (
          <div key={label} className="flex items-center gap-3.5">
            {i > 0 && <span className="h-3 w-px bg-border" />}
            <span className="text-base">
              <span className="font-semibold tabular-nums">{n}</span>{" "}
              <span className="text-muted-foreground">{label}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Quick actions: structured capture (wired to existing dialogs) ──────────
// Color-Plus-Token: each action carries a rationed tinted icon chip, paired
// with its label — mirrors the design reference's QuickActions.
function QuickAction({
  icon: Icon,
  label,
  tint,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  tint: string;
  onClick: () => void;
}) {
  return (
    <Button variant="outline" className="h-9 gap-2 pl-2 pr-3.5" onClick={onClick}>
      <span className={cn("flex size-5 shrink-0 items-center justify-center rounded-sm", tint)}>
        <Icon className="size-3.5" />
      </span>
      {label}
    </Button>
  );
}

function QuickActions({
  onAddSymptom,
  onAddCondition,
  onAddFlag,
  onAddAllergy,
  onAddMedication,
}: {
  onAddSymptom: () => void;
  onAddCondition: () => void;
  onAddFlag: () => void;
  onAddAllergy: () => void;
  onAddMedication: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <QuickAction
        icon={Brain}
        label="Add diagnosis"
        tint="bg-violet-500/15 text-violet-600 dark:text-violet-400"
        onClick={onAddCondition}
      />
      <QuickAction
        icon={Activity}
        label="Record symptom"
        tint="bg-blue-500/15 text-blue-600 dark:text-blue-400"
        onClick={onAddSymptom}
      />
      <QuickAction
        icon={Flag}
        label="Add flag"
        tint="bg-amber-500/15 text-amber-600 dark:text-amber-500"
        onClick={onAddFlag}
      />
      <QuickAction
        icon={TriangleAlert}
        label="Add allergy"
        tint="bg-red-500/15 text-red-600 dark:text-red-400"
        onClick={onAddAllergy}
      />
      <QuickAction
        icon={Pill}
        label="Add medication"
        tint="bg-green-500/15 text-green-600 dark:text-green-400"
        onClick={onAddMedication}
      />
    </div>
  );
}
